import React, { useEffect, useState } from "react";
import { FileText, Copy, Trash2, Search, Plus, MessageCircle } from "lucide-react";
import { listQuotes, deleteQuote, saveQuote } from "../lib/api";
import { brl, computeTotals } from "../lib/format";
import { GoogleDots } from "./GoogleBits";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

export default function SavedQuotes({ companyId, onOpen, onNew }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setQuotes(await listQuotes(companyId));
    } catch {
      toast.error("Erro ao carregar orçamentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [companyId]); // eslint-disable-line

  const remove = async (id) => {
    try {
      await deleteQuote(id);
      setQuotes((qs) => qs.filter((x) => x.id !== id));
      toast.success("Orçamento excluído");
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const duplicate = async (quote) => {
    try {
      const { id, created_at, updated_at, ...rest } = quote;
      const res = await saveQuote({ ...rest, status: "Rascunho", title: `${quote.title} (cópia)` });
      setQuotes((qs) => [res, ...qs]);
      toast.success("Orçamento duplicado");
    } catch {
      toast.error("Erro ao duplicar");
    }
  };

  const filtered = quotes.filter((x) => {
    return !q || (x.client_name || "").toLowerCase().includes(q.toLowerCase()) || (x.title || "").toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="min-h-screen pt-14 px-5">
      <div className="max-w-3xl mx-auto pt-12 pb-20">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Meus orçamentos</h1>
            <p className="text-slate-500 mt-1.5 text-[14px]">{quotes.length} {quotes.length === 1 ? "orçamento salvo" : "orçamentos salvos"}</p>
          </div>
          <Button onClick={onNew} data-testid="new-from-saved" className="rounded-full h-10 bg-slate-900 hover:bg-slate-800 gap-1.5">
            <Plus size={16} /> Novo orçamento
          </Button>
        </div>

        <div className="mt-8">
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-quotes" placeholder="Buscar por cliente ou título" className="pl-10 h-11 rounded-full border-slate-200 bg-white" />
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="flex justify-center py-20"><GoogleDots /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 float-up">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mb-4">
                <FileText size={22} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-[14px]">Nenhum orçamento por aqui ainda.</p>
              <Button onClick={onNew} variant="outline" className="rounded-full mt-5 border-slate-200">Criar o primeiro</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((quote) => {
                const total = computeTotals(quote.data || {}).total;
                return (
                  <div key={quote.id} data-testid={`quote-card-${quote.id}`} className="group bg-white ring-1 ring-slate-200 hover:ring-slate-300 rounded-2xl px-5 py-4 flex items-center gap-4 transition-all float-up">
                    <button onClick={() => onOpen(quote)} className="flex-1 text-left min-w-0">
                      <div className="flex items-center gap-2.5">
                        <span className="text-[15px] font-semibold text-slate-900 truncate">{quote.title || "Sem título"}</span>
                        {quote.source === "whatsapp" && (
                          <span data-testid="quote-source-whatsapp" className="inline-flex items-center gap-1 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#128C7E] bg-[#25D366]/10 rounded-full px-2 py-0.5">
                            <MessageCircle size={10} /> WhatsApp
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-slate-400 mt-1 truncate">
                        {quote.client_name || "Sem cliente"} · <span className="font-mono-jb text-slate-500">{brl(total)}</span>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => duplicate(quote)} data-testid={`duplicate-${quote.id}`} title="Duplicar" className="grid place-items-center w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500">
                        <Copy size={15} />
                      </button>
                      <button onClick={() => remove(quote.id)} data-testid={`delete-${quote.id}`} title="Excluir" className="grid place-items-center w-8 h-8 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-500">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
